import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/buttons';
import { Modals } from '@/helpers/modals';
import { useNavigation } from '@/hooks/navigation';
import { BonanzaPromoStatus } from '@/modules/promotions/helpers';
import { useBonanzaPromo } from '@/modules/promotions/hooks';
import { useGetCommonStore, useModalsStore } from '@/store';
import { ScCtaButton } from './styled';

interface CtaButtonProps {
  className?: string;
}

export const CtaButton: React.FC<CtaButtonProps> = ({ className }) => {
  const { t } = useTranslation(['common', 'bonanzaPromo']);
  const open = useModalsStore.use.open();
  const { navigate } = useNavigation();

  const useCommonStore = useGetCommonStore();
  const userBonanzaGroup = useCommonStore.use.userBonanzaGroup();
  const { isBonusIssued, isLockedPromo, handleAcknowledgeBonus } =
    useBonanzaPromo();

  const handleClick = useCallback(() => {
    if (userBonanzaGroup === BonanzaPromoStatus.UNLOGGED) {
      return open(Modals.Registration);
    }

    if (isBonusIssued) {
      handleAcknowledgeBonus();
      return navigate('/promotions?tab=active-bonuses');
    }
    if (isLockedPromo) {
      return navigate('/');
    }

    return navigate('/games/reward_rush#gamesSection');
  }, [
    handleAcknowledgeBonus,
    isBonusIssued,
    navigate,
    open,
    userBonanzaGroup,
    isLockedPromo,
  ]);

  const buttonText = useMemo(() => {
    if (userBonanzaGroup === BonanzaPromoStatus.UNLOGGED) {
      return t('common:button.register');
    }
    if (userBonanzaGroup === BonanzaPromoStatus.PROMO_LOCKED) {
      return t('bonanzaPromo:backToLobby');
    }
    if (userBonanzaGroup === BonanzaPromoStatus.LEVEL_4) {
      return t('bonanzaPromo:claimBonus');
    }

    return t('bonanzaPromo:enjoyTheGames');
  }, [t, userBonanzaGroup]);

  return (
    <ScCtaButton className={className || 'promoButton'}>
      <Button onClick={handleClick} primary>
        {buttonText}
      </Button>
    </ScCtaButton>
  );
};
